import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDiligenciasBasePath } from '@/features/diligencias/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext.hook';
import { useToast } from '@/hooks/use-toast';
import { type Diligencia, type DiligenciaEtapa, type DiligenciaStatus } from '@/features/diligencias/hooks';
import { ActivityPanel } from '@/components/incorporador/ActivityPanel';
import { ArrowStepperWorkflow } from '@/components/incorporador/ArrowStepperWorkflow';
import { DiligenciaSignatureDocuments } from '@/components/incorporador/DiligenciaSignatureDocuments';
import {
  useDiligenciaDocuments,
  type DiligenciaDocument,
  type DiligenciaDocumentReviewStatus,
} from '@/features/diligencias/hooks';
import { useClicksign, type ClicksignSigner } from '@/hooks/useClicksign';
import {
  ETAPA_LABELS,
  ETAPAS_ORDENADAS,
  DILIGENCIA_CHECKLISTS,
  AREA_STATUS_LABELS,
  ETAPA_TO_AREA_STATUS_KEY,
  ETAPA_VISUAL_LABELS,
  getAreaStatus,
  getAreaStatusKeysForEspecialidade,
  getNextGrupoFirstEtapa,
  getEtapasDoGrupoAtual,
  isGrupoVisualConcluido,
  mapEtapaToVisual,
  hasEspecialistaForEtapa,
  getAreaResponsavelLabel,
  type AreaStatus,
  type DiligenciaAreaStatuses,
  type DiligenciaEtapa as DiligenciaEtapaCanonical,
} from '@/utils/diligenciaChecklists';
import { useDiligenciaPermissions } from '@/features/diligencias/hooks';
import { getEspecialidadeConfig } from '@/utils/adminEspecializadoConfig';
import { DiligenciaDocumentChecklist } from '@/components/incorporador/DiligenciaDocumentChecklist';
import { BaseModal } from '@/components/shared/modals';
import {
  ArrowLeft,
  Building2,
  CheckCircle2,
  Circle,
  Loader2,
  MapPin,
  Calendar,
  DollarSign,
  FileText,
  Hash,
  Ruler,
  Clock,
  ChevronLeft,
  ChevronRight,
  User,
  Mail,
  Phone,
  Pencil,
  Save,
  X,
} from 'lucide-react';

// =============================================================================
// CONSTANTES
// =============================================================================

const statusColors: Record<DiligenciaStatus, string> = {
  rascunho: 'bg-gray-100 text-gray-700',
  pendente: 'bg-yellow-100 text-yellow-700',
  em_analise: 'bg-blue-100 text-blue-700',
  doc_pendente: 'bg-orange-100 text-orange-700',
  aprovado: 'bg-green-100 text-green-700',
  reprovado: 'bg-red-100 text-red-700',
  cancelado: 'bg-gray-100 text-gray-500',
};

const statusLabels: Record<DiligenciaStatus, string> = {
  rascunho: 'Rascunho',
  pendente: 'Pendente',
  em_analise: 'Em Analise',
  doc_pendente: 'Doc. Pendente',
  aprovado: 'Aprovado',
  reprovado: 'Reprovado',
  cancelado: 'Cancelado',
};

/** Etapas formatadas para o ArrowStepperWorkflow */
const ETAPAS_STEPPER = ETAPAS_ORDENADAS.map((key) => ({
  key,
  label: ETAPA_LABELS[key],
}));

// =============================================================================
// COMPONENTES AUXILIARES
// =============================================================================

/**
 * Campo de informação com label, valor e ícone opcional.
 * Retorna null se o valor for vazio ou nulo.
 */
const InfoField: React.FC<{
  label: string;
  value: string | number | null | undefined;
  icon?: React.ReactNode;
  format?: 'currency' | 'date' | 'number' | 'text';
}> = ({ label, value, icon, format = 'text' }) => {
  if (value === null || value === undefined || value === '') return null;

  let displayValue: string;
  switch (format) {
    case 'currency':
      displayValue = formatCurrency(Number(value));
      break;
    case 'date':
      displayValue = formatDate(String(value));
      break;
    case 'number':
      displayValue = Number(value).toLocaleString('pt-BR');
      break;
    default:
      displayValue = String(value);
  }

  return (
    <div className="flex items-start gap-2">
      {icon && <span className="text-muted-foreground mt-0.5">{icon}</span>}
      <div>
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="font-medium">{displayValue}</p>
      </div>
    </div>
  );
};

/**
 * Campo editável inline: mostra InfoField em modo leitura, Input em modo edição.
 */
const EditableField: React.FC<{
  label: string;
  fieldKey: string;
  icon?: React.ReactNode;
  format?: 'currency' | 'date' | 'number' | 'text';
  type?: string;
  isEditing: boolean;
  editData: Record<string, string>;
  onChange: (key: string, value: string) => void;
  originalValue: string | number | null | undefined;
}> = ({ label, fieldKey, icon, format = 'text', type = 'text', isEditing, editData, onChange, originalValue }) => {
  if (isEditing) {
    return (
      <div className="space-y-1">
        <Label className="text-xs text-muted-foreground">{label}</Label>
        <Input
          type={type}
          value={editData[fieldKey] ?? ''}
          onChange={(e) => onChange(fieldKey, e.target.value)}
          className="h-8 text-sm"
        />
      </div>
    );
  }

  // Modo leitura: usa InfoField existente
  return <InfoField label={label} value={originalValue} icon={icon} format={format} />;
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

const AdminDiligenciaDetalhes: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const diligenciasBasePath = useDiligenciasBasePath();
  const { user } = useAuth();
  const { toast } = useToast();
  const { canAdvanceRetreat, canApproveReject, canAccessEtapa, isAdminEspecializado, especialidade, etapasPermitidas } = useDiligenciaPermissions();
  const espConfig = getEspecialidadeConfig(especialidade);

  const [diligencia, setDiligencia] = useState<Diligencia | null>(null);
  const [incorporadoraData, setIncorporadoraData] = useState<{
    cnpj: string;
    razao_social: string;
    nome_fantasia: string | null;
    contato_legal_nome: string | null;
    contato_legal_email: string | null;
    contato_legal_telefone: string | null;
    contato_financeiro_nome: string | null;
    contato_financeiro_email: string | null;
    contato_financeiro_telefone: string | null;
    status: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);

  // Inline editing
  const [isEditing, setIsEditing] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editData, setEditData] = useState<Record<string, string>>({});

  // BravaForte signer fields
  const [bravaforteNome, setBravaforteNome] = useState('');
  const [bravaforteEmail, setBravaforteEmail] = useState('');
  const [isSavingSigner, setIsSavingSigner] = useState(false);

  const {
    documents,
    fetchDocuments,
    getSignedUrl,
    updateDocumentReview,
  } = useDiligenciaDocuments(id);

  const { sendToClicksign, isSending: isSendingClicksign } = useClicksign();

  // Estado do modal de envio para ClickSign
  const [clicksignModalOpen, setClicksignModalOpen] = useState(false);
  const [clicksignDocTarget, setClicksignDocTarget] = useState<DiligenciaDocument | null>(null);
  const [sendingDocumentId, setSendingDocumentId] = useState<string | null>(null);
  const [clicksignSigners, setClicksignSigners] = useState<ClicksignSigner[]>([
    { name: '', email: '', role: 'contractor' },
    { name: '', email: '', role: 'contractee' },
  ]);

  // ---------------------------------------------------------------------------
  // Busca de dados
  // ---------------------------------------------------------------------------

  const fetchDiligencia = async () => {
    if (!id) {
      setError('ID da diligencia nao informado');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('diligencias')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      setDiligencia(data);

      // Buscar dados da incorporadora vinculada
      if (data.incorporadora_id) {
        const { data: incData } = await supabase
          .from('incorporadoras')
          .select('cnpj, razao_social, nome_fantasia, contato_legal_nome, contato_legal_email, contato_legal_telefone, contato_financeiro_nome, contato_financeiro_email, contato_financeiro_telefone, status')
          .eq('id', data.incorporadora_id)
          .maybeSingle();

        setIncorporadoraData(incData);
      }
    } catch (err) {
      console.error('Erro ao buscar diligencia:', err);
      setError('Diligencia nao encontrada');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDiligencia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (diligencia) {
      fetchDocuments();
      setBravaforteNome(diligencia.bravaforte_signatario_nome || '');
      setBravaforteEmail(diligencia.bravaforte_signatario_email || '');
    }
  }, [diligencia, fetchDocuments]);

  const handleViewDocument = async (filePath: string) => {
    const url = await getSignedUrl(filePath);
    if (url) {
      window.open(url, '_blank');
    }
  };

  // ---------------------------------------------------------------------------
  // Inline editing helpers
  // ---------------------------------------------------------------------------

  const startEditing = () => {
    if (!diligencia) return;
    setEditData({
      spe_razao_social: diligencia.spe_razao_social || '',
      spe_cnpj: diligencia.spe_cnpj || '',
      spe_endereco_fiscal: diligencia.spe_endereco_fiscal || '',
      empreendimento_nome: diligencia.empreendimento_nome || '',
      empreendimento_endereco: diligencia.empreendimento_endereco || '',
      empreendimento_numero: diligencia.empreendimento_numero || '',
      empreendimento_cidade: diligencia.empreendimento_cidade || '',
      empreendimento_estado: diligencia.empreendimento_estado || '',
      empreendimento_cep: diligencia.empreendimento_cep || '',
      tipo_imovel: diligencia.tipo_imovel || '',
      terreno_matriculas: Array.isArray(diligencia.terreno_matriculas) ? diligencia.terreno_matriculas.join(', ') : (diligencia.terreno_matriculas || ''),
      contato_representante_nome: diligencia.contato_representante_nome || '',
      contato_representante_email: diligencia.contato_representante_email || '',
      contato_representante_telefone: diligencia.contato_representante_telefone || '',
      contato_financeiro_nome: diligencia.contato_financeiro_nome || '',
      contato_financeiro_email: diligencia.contato_financeiro_email || '',
      contato_financeiro_telefone: diligencia.contato_financeiro_telefone || '',
      valor_captacao: diligencia.valor_captacao?.toString() || '',
      valor_medio_m2_lancamento: diligencia.valor_medio_m2_lancamento?.toString() || '',
      soma_m2_unidades: diligencia.soma_m2_unidades?.toString() || '',
      soma_m2_vendavel: diligencia.soma_m2_vendavel?.toString() || '',
      numero_unidades: diligencia.numero_unidades?.toString() || '',
      tamanho_unidades_m2: diligencia.tamanho_unidades_m2 || '',
      prazo_meses: diligencia.prazo_meses?.toString() || '',
      data_prevista_lancamento: diligencia.data_prevista_lancamento || '',
      data_prevista_entrega: diligencia.data_prevista_entrega || '',
    });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditData({});
  };

  const updateEditField = (key: string, value: string) => {
    setEditData((prev) => ({ ...prev, [key]: value }));
  };

  const saveEditing = async () => {
    if (!diligencia || !id) return;
    setIsSavingEdit(true);

    try {
      // Detectar campos alterados
      const changes: Record<string, unknown> = {};
      const changeDescriptions: string[] = [];

      const fieldLabels: Record<string, string> = {
        spe_razao_social: 'SPE Razão Social',
        spe_cnpj: 'SPE CNPJ',
        spe_endereco_fiscal: 'SPE Endereço Fiscal',
        empreendimento_nome: 'Nome do Empreendimento',
        empreendimento_endereco: 'Endereço',
        empreendimento_numero: 'Número',
        empreendimento_cidade: 'Cidade',
        empreendimento_estado: 'Estado',
        empreendimento_cep: 'CEP',
        tipo_imovel: 'Tipo de Imóvel',
        terreno_matriculas: 'Matrículas do Terreno',
        contato_representante_nome: 'Nome do Representante',
        contato_representante_email: 'Email do Representante',
        contato_representante_telefone: 'Telefone do Representante',
        contato_financeiro_nome: 'Nome Contato Financeiro',
        contato_financeiro_email: 'Email Contato Financeiro',
        contato_financeiro_telefone: 'Telefone Contato Financeiro',
        valor_captacao: 'Valor Captação',
        valor_medio_m2_lancamento: 'Valor Médio m²',
        soma_m2_unidades: 'Soma m² Unidades',
        soma_m2_vendavel: 'Soma m² Vendável',
        numero_unidades: 'Nº Unidades',
        tamanho_unidades_m2: 'Tamanho Unidades',
        prazo_meses: 'Prazo (meses)',
        data_prevista_lancamento: 'Data Lançamento',
        data_prevista_entrega: 'Data Entrega',
      };

      // Campos numéricos
      const numericFields = ['valor_captacao', 'valor_medio_m2_lancamento', 'soma_m2_unidades', 'soma_m2_vendavel', 'numero_unidades', 'prazo_meses'];

      for (const [key, newValue] of Object.entries(editData)) {
        const oldValue = (diligencia as Record<string, unknown>)[key];
        const oldStr = oldValue != null ? String(oldValue) : '';

        if (newValue !== oldStr) {
          if (numericFields.includes(key)) {
            changes[key] = newValue ? Number(newValue) : null;
          } else {
            changes[key] = newValue || null;
          }
          changeDescriptions.push(fieldLabels[key] || key);
        }
      }

      if (Object.keys(changes).length === 0) {
        toast({ title: 'Nenhuma alteração', description: 'Nenhum campo foi modificado.' });
        setIsEditing(false);
        return;
      }

      const { error: updateError } = await supabase
        .from('diligencias')
        .update(changes)
        .eq('id', id);

      if (updateError) throw updateError;

      // Registrar no histórico de atividade
      await supabase.from('diligencia_messages').insert({
        diligencia_id: id,
        sender_id: user?.id,
        type: 'system',
        message: `Dados alterados pelo admin: ${changeDescriptions.join(', ')}`,
      });

      toast({ title: 'Dados atualizados', description: `${changeDescriptions.length} campo(s) alterado(s) com sucesso.` });
      setIsEditing(false);
      setEditData({});
      fetchDiligencia();
    } catch (err) {
      console.error('Erro ao salvar edição:', err);
      toast({ title: 'Erro', description: 'Não foi possível salvar as alterações.', variant: 'destructive' });
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleSaveBravaforteSigner = async () => {
    if (!diligencia) return;

    setIsSavingSigner(true);
    try {
      const { error: updateError } = await supabase
        .from('diligencias')
        .update({
          bravaforte_signatario_nome: bravaforteNome.trim() || null,
          bravaforte_signatario_email: bravaforteEmail.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', diligencia.id);

      if (updateError) throw updateError;

      setDiligencia(prev => prev ? {
        ...prev,
        bravaforte_signatario_nome: bravaforteNome.trim() || null,
        bravaforte_signatario_email: bravaforteEmail.trim() || null,
      } : null);

      toast({
        title: 'Signatario atualizado',
        description: 'Os dados do signatario BravaForte foram salvos.',
      });
    } catch (err) {
      console.error('Erro ao salvar signatario BravaForte:', err);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel salvar os dados do signatario.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingSigner(false);
    }
  };

  const handleReviewDocument = async (
    docId: string,
    status: DiligenciaDocumentReviewStatus,
    note?: string | null
  ) => {
    if (!diligencia) return false;

    setReviewLoading(true);
    try {
      const ok = await updateDocumentReview(docId, status, note);
      if (!ok) return false;

      if (status === 'reprovado') {
        const { error: statusError } = await supabase
          .from('diligencias')
          .update({
            status: 'doc_pendente',
            updated_at: new Date().toISOString(),
          })
          .eq('id', diligencia.id);

        if (statusError) throw statusError;
      }

      const doc = documents.find((item) => item.id === docId);
      const docLabel = doc?.file_name ?? 'Documento';
      const etapaLabel = ETAPA_LABELS[(doc?.etapa ?? diligencia.etapa_atual) as DiligenciaEtapa];
      const noteSuffix =
        status === 'reprovado' && note?.trim()
          ? `. Motivo: ${note.trim()}`
          : '';

      await logSystemMessage(
        `${docLabel} ${status === 'aprovado' ? 'aprovado' : 'reprovado'} na etapa ${etapaLabel}${noteSuffix}`
      );

      await fetchDiligencia();
      return true;
    } catch (err) {
      console.error('Erro ao revisar documento no detalhe admin:', err);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel concluir a revisão do documento.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setReviewLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Helper: registra acao no painel de atividade
  // ---------------------------------------------------------------------------

  const logSystemMessage = async (message: string) => {
    if (!id || !user) return;
    try {
      const { error: msgError } = await supabase.from('diligencia_messages').insert({
        diligencia_id: id,
        sender_id: user.id,
        message,
        type: 'system',
        metadata: {},
      });
      if (msgError) {
        console.warn('Erro ao registrar mensagem de sistema:', msgError);
      }
    } catch (err) {
      console.warn('Erro ao registrar mensagem de sistema:', err);
    }
  };

  // ---------------------------------------------------------------------------
  // Ações administrativas
  // ---------------------------------------------------------------------------

  const handleAvancar = async () => {
    if (!diligencia) return;

    const currentIndex = ETAPAS_ORDENADAS.indexOf(diligencia.etapa_atual);
    if (currentIndex === ETAPAS_ORDENADAS.length - 1) {
      toast({
        title: 'Aviso',
        description: 'A diligencia ja esta na ultima etapa.',
        variant: 'destructive',
      });
      return;
    }

    const nextEtapa = ETAPAS_ORDENADAS[currentIndex + 1];
    setActionLoading(true);

    try {
      const { error: updateError } = await supabase
        .from('diligencias')
        .update({
          etapa_atual: nextEtapa,
          status: 'pendente',
          updated_at: new Date().toISOString(),
        })
        .eq('id', diligencia.id);

      if (updateError) throw updateError;

      await logSystemMessage(`Etapa avancada para: ${ETAPA_LABELS[nextEtapa]}`);

      toast({
        title: 'Etapa avancada',
        description: `Diligencia movida para: ${ETAPA_LABELS[nextEtapa]}`,
      });

      await fetchDiligencia();
    } catch (err) {
      console.error('Erro ao avancar etapa:', err);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel avancar a etapa.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetroceder = async () => {
    if (!diligencia) return;

    const currentIndex = ETAPAS_ORDENADAS.indexOf(diligencia.etapa_atual);
    if (currentIndex === 0) {
      toast({
        title: 'Aviso',
        description: 'A diligencia ja esta na primeira etapa.',
        variant: 'destructive',
      });
      return;
    }

    const prevEtapa = ETAPAS_ORDENADAS[currentIndex - 1];
    setActionLoading(true);

    try {
      const { error: updateError } = await supabase
        .from('diligencias')
        .update({
          etapa_atual: prevEtapa,
          status: 'pendente',
          updated_at: new Date().toISOString(),
        })
        .eq('id', diligencia.id);

      if (updateError) throw updateError;

      await logSystemMessage(`Etapa retrocedida para: ${ETAPA_LABELS[prevEtapa]}`);

      toast({
        title: 'Etapa retrocedida',
        description: `Diligencia movida para: ${ETAPA_LABELS[prevEtapa]}`,
      });

      await fetchDiligencia();
    } catch (err) {
      console.error('Erro ao retroceder etapa:', err);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel retroceder a etapa.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAprovar = async () => {
    if (!diligencia) return;

    setActionLoading(true);

    try {
      const { error: updateError } = await supabase
        .from('diligencias')
        .update({
          status: 'aprovado',
          updated_at: new Date().toISOString(),
        })
        .eq('id', diligencia.id);

      if (updateError) throw updateError;

      await logSystemMessage('Diligencia aprovada');

      toast({
        title: 'Diligencia aprovada',
        description: 'O status foi atualizado para Aprovado.',
      });

      await fetchDiligencia();
    } catch (err) {
      console.error('Erro ao aprovar diligencia:', err);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel aprovar a diligencia.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReprovar = async () => {
    if (!diligencia) return;

    setActionLoading(true);

    try {
      const { error: updateError } = await supabase
        .from('diligencias')
        .update({
          status: 'reprovado',
          updated_at: new Date().toISOString(),
        })
        .eq('id', diligencia.id);

      if (updateError) throw updateError;

      await logSystemMessage('Diligencia reprovada');

      toast({
        title: 'Diligencia reprovada',
        description: 'O status foi atualizado para Reprovado.',
        variant: 'destructive',
      });

      await fetchDiligencia();
    } catch (err) {
      console.error('Erro ao reprovar diligencia:', err);
      toast({
        title: 'Erro',
        description: 'Nao foi possivel reprovar a diligencia.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Atualizar status da área (admin especializado)
  // ---------------------------------------------------------------------------

  /**
   * Extrai os area statuses da diligência atual (cast seguro).
   */
  const extractAreaStatuses = (d: typeof diligencia): DiligenciaAreaStatuses => {
    const raw = d as unknown as Record<string, unknown>;
    return {
      area_tecnica_economica_status: (raw.area_tecnica_economica_status as AreaStatus) || 'pendente',
      area_juridica_status: (raw.area_juridica_status as AreaStatus) || 'pendente',
      area_contabil_status: (raw.area_contabil_status as AreaStatus) || 'pendente',
      area_mercadologica_status: (raw.area_mercadologica_status as AreaStatus) || 'pendente',
    };
  };

  const handleUpdateAreaStatus = async (newStatus: AreaStatus) => {
    if (!diligencia || !especialidade) return;

    const keys = getAreaStatusKeysForEspecialidade(especialidade);
    if (keys.length === 0) return;

    setActionLoading(true);
    try {
      // 1. Atualizar status da(s) área(s) da especialidade
      const updatePayload: Record<string, string> = {};
      for (const key of keys) {
        updatePayload[key] = newStatus;
      }
      updatePayload.updated_at = new Date().toISOString();

      const { error: updateError } = await supabase
        .from('diligencias')
        .update(updatePayload)
        .eq('id', diligencia.id);

      if (updateError) throw updateError;

      const statusLabel = AREA_STATUS_LABELS[newStatus];
      await logSystemMessage(`Área ${espConfig?.label ?? especialidade} marcada como: ${statusLabel}`);

      // 2. Se a conclusão da área completa o grupo, avançar etapa_atual
      if (newStatus === 'concluida') {
        // Simular o novo estado dos statuses (com a mudança que acabamos de persistir)
        const currentStatuses = extractAreaStatuses(diligencia);
        const simulatedStatuses: DiligenciaAreaStatuses = { ...currentStatuses };
        for (const key of keys) {
          (simulatedStatuses as Record<string, AreaStatus>)[key] = 'concluida';
        }

        const grupoAtualKey = mapEtapaToVisual(diligencia.etapa_atual);
        const grupoCompleto = isGrupoVisualConcluido(grupoAtualKey, simulatedStatuses);

        if (grupoCompleto) {
          const grupoLabel = ETAPA_VISUAL_LABELS[grupoAtualKey];
          await logSystemMessage(`Grupo "${grupoLabel}" concluído — todas as áreas finalizadas`);

          // Verificar se existe próximo grupo
          const nextEtapa = getNextGrupoFirstEtapa(diligencia.etapa_atual);
          if (nextEtapa) {
            const { error: advanceError } = await supabase
              .from('diligencias')
              .update({
                etapa_atual: nextEtapa,
                status: 'pendente',
                updated_at: new Date().toISOString(),
              })
              .eq('id', diligencia.id);

            if (advanceError) {
              console.error('Erro ao avançar etapa:', advanceError);
            } else {
              await logSystemMessage(`Diligência avançada para: ${ETAPA_LABELS[nextEtapa]}`);
              toast({
                title: 'Grupo concluído!',
                description: `Diligência avançou para ${ETAPA_LABELS[nextEtapa]}`,
              });
            }
          } else {
            toast({
              title: 'Grupo concluído!',
              description: 'Todas as áreas foram finalizadas.',
            });
          }
        } else {
          toast({
            title: 'Área concluída',
            description: `${espConfig?.label ?? 'Área'} finalizada. Aguardando demais áreas do grupo.`,
          });
        }
      } else {
        toast({
          title: 'Status da área atualizado',
          description: `Área marcada como: ${AREA_STATUS_LABELS[newStatus]}`,
        });
      }

      await fetchDiligencia();
    } catch (err) {
      console.error('Erro ao atualizar status da área:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o status da área.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  /**
   * Atualiza o status de uma área específica (usado pelo admin principal).
   * Diferente de handleUpdateAreaStatus que atualiza por especialidade.
   */
  const handleUpdateSingleAreaStatus = async (
    etapa: DiligenciaEtapaCanonical,
    newStatus: AreaStatus
  ) => {
    if (!diligencia) return;

    const areaKey = ETAPA_TO_AREA_STATUS_KEY[etapa];
    if (!areaKey) return;

    setActionLoading(true);
    try {
      const updatePayload: Record<string, string> = {
        [areaKey]: newStatus,
        updated_at: new Date().toISOString(),
      };

      const { error: updateError } = await supabase
        .from('diligencias')
        .update(updatePayload)
        .eq('id', diligencia.id);

      if (updateError) throw updateError;

      const etapaLabel = ETAPA_LABELS[etapa];
      const statusLabel = AREA_STATUS_LABELS[newStatus];
      await logSystemMessage(`Área ${etapaLabel} marcada como: ${statusLabel}`);

      // Verificar se o grupo ficou completo após essa mudança
      if (newStatus === 'concluida') {
        const currentStatuses = extractAreaStatuses(diligencia);
        const simulated: DiligenciaAreaStatuses = { ...currentStatuses };
        (simulated as Record<string, AreaStatus>)[areaKey] = 'concluida';

        const grupoAtualKey = mapEtapaToVisual(diligencia.etapa_atual);
        const grupoCompleto = isGrupoVisualConcluido(grupoAtualKey, simulated);

        if (grupoCompleto) {
          const grupoLabel = ETAPA_VISUAL_LABELS[grupoAtualKey];
          await logSystemMessage(`Grupo "${grupoLabel}" concluído — todas as áreas finalizadas`);

          const nextEtapa = getNextGrupoFirstEtapa(diligencia.etapa_atual);
          if (nextEtapa) {
            const { error: advanceError } = await supabase
              .from('diligencias')
              .update({
                etapa_atual: nextEtapa,
                status: 'pendente',
                updated_at: new Date().toISOString(),
              })
              .eq('id', diligencia.id);

            if (!advanceError) {
              await logSystemMessage(`Diligência avançada para: ${ETAPA_LABELS[nextEtapa]}`);
              toast({
                title: 'Grupo concluído!',
                description: `Diligência avançou para ${ETAPA_LABELS[nextEtapa]}`,
              });
            }
          } else {
            toast({ title: 'Grupo concluído!', description: 'Todas as áreas foram finalizadas.' });
          }
        } else {
          toast({
            title: 'Área concluída',
            description: `${etapaLabel} finalizada. Aguardando demais áreas do grupo.`,
          });
        }
      } else {
        toast({
          title: 'Status atualizado',
          description: `${ETAPA_LABELS[etapa]}: ${AREA_STATUS_LABELS[newStatus]}`,
        });
      }

      await fetchDiligencia();
    } catch (err) {
      console.error('Erro ao atualizar status da área:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível atualizar o status da área.',
        variant: 'destructive',
      });
    } finally {
      setActionLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // ClickSign: abrir modal e enviar
  // ---------------------------------------------------------------------------

  const handleOpenClicksignModal = (doc: DiligenciaDocument) => {
    setClicksignDocTarget(doc);

    // Pre-preencher signatarios com dados da diligencia (se disponivel)
    const prefilledSigners: ClicksignSigner[] = [
      {
        name: diligencia?.contato_representante_nome || '',
        email: diligencia?.contato_representante_email || '',
        role: 'contractee',
      },
      { name: '', email: '', role: 'contractor' },
    ];
    setClicksignSigners(prefilledSigners);
    setClicksignModalOpen(true);
  };

  const handleConfirmClicksignSend = async () => {
    if (!clicksignDocTarget || !diligencia) return;

    const validSigners = clicksignSigners.filter(s => s.name.trim() && s.email.trim());
    if (validSigners.length === 0) {
      toast({
        title: 'Preencha os signatarios',
        description: 'Informe ao menos um signatario com nome e email.',
        variant: 'destructive',
      });
      return;
    }

    setSendingDocumentId(clicksignDocTarget.id);
    setClicksignModalOpen(false);

    const result = await sendToClicksign({
      diligencia_id: diligencia.id,
      document_id: clicksignDocTarget.id,
      signers: validSigners,
    });

    if (result) {
      await fetchDocuments();
    }

    setSendingDocumentId(null);
    setClicksignDocTarget(null);
  };

  const updateSigner = (index: number, field: keyof ClicksignSigner, value: string) => {
    setClicksignSigners(prev =>
      prev.map((s, i) => (i === index ? { ...s, [field]: value } : s))
    );
  };

  // ---------------------------------------------------------------------------
  // Estados de carregamento e erro
  // ---------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-16 w-full" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Skeleton className="h-56" />
              <Skeleton className="h-56" />
            </div>
            <Skeleton className="h-48" />
          </div>
          <Skeleton className="h-[500px]" />
        </div>
      </div>
    );
  }

  if (error || !diligencia) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(diligenciasBasePath)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Erro</h1>
            <p className="text-muted-foreground">{error || 'Diligencia nao encontrada'}</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-xl font-semibold mb-2">Diligencia nao encontrada</h3>
            <p className="text-muted-foreground mb-6">
              A diligencia solicitada nao foi encontrada ou voce nao tem permissao para visualiza-la.
            </p>
            <Button variant="primary" onClick={() => navigate(diligenciasBasePath)}>
              Voltar para Diligencias
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentEtapaIndex = ETAPAS_ORDENADAS.indexOf(diligencia.etapa_atual);
  const isFirstEtapa = currentEtapaIndex === 0;
  const isLastEtapa = currentEtapaIndex === ETAPAS_ORDENADAS.length - 1;
  const isTerminal =
    diligencia.status === 'aprovado' || diligencia.status === 'reprovado';

  // ---------------------------------------------------------------------------
  // Renderização principal
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(diligenciasBasePath)}
            aria-label="Voltar para lista de diligencias"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {diligencia.empreendimento_nome}
            </h1>
            <p className="text-muted-foreground">
              {diligencia.spe_razao_social || 'SPE nao informada'}
            </p>
          </div>
        </div>
        <Badge className={cn('text-sm px-3 py-1', statusColors[diligencia.status])}>
          {statusLabels[diligencia.status]}
        </Badge>
      </div>

      {/* Stepper de etapas (apenas admin principal) */}
      {!isAdminEspecializado && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Progresso da Diligencia</CardTitle>
            <CardDescription>Etapa atual e historico de avanco</CardDescription>
          </CardHeader>
          <CardContent>
            <ArrowStepperWorkflow
              etapas={ETAPAS_STEPPER}
              etapaAtual={diligencia.etapa_atual}
              status={diligencia.status}
            />
          </CardContent>
        </Card>
      )}

      {/* Barra de ações administrativas */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground mr-1">
              Ações:
            </span>

            {canAdvanceRetreat && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRetroceder}
                  disabled={actionLoading || isFirstEtapa}
                  className="flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retroceder Etapa
                </Button>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleAvancar}
                  disabled={actionLoading || isLastEtapa}
                  className="flex items-center gap-1"
                >
                  Avançar Etapa
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </>
            )}

            {canApproveReject && !isTerminal && (
              <>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleAprovar}
                  disabled={actionLoading}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Aprovar
                </Button>

                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleReprovar}
                  disabled={actionLoading}
                >
                  Reprovar
                </Button>
              </>
            )}

            {isAdminEspecializado && espConfig && diligencia && (() => {
              const areaStatuses: DiligenciaAreaStatuses = {
                area_tecnica_economica_status: (diligencia as Record<string, unknown>).area_tecnica_economica_status as AreaStatus ?? 'pendente',
                area_juridica_status: (diligencia as Record<string, unknown>).area_juridica_status as AreaStatus ?? 'pendente',
                area_contabil_status: (diligencia as Record<string, unknown>).area_contabil_status as AreaStatus ?? 'pendente',
                area_mercadologica_status: (diligencia as Record<string, unknown>).area_mercadologica_status as AreaStatus ?? 'pendente',
              };
              const currentAreaStatus = getAreaStatus(areaStatuses, diligencia.etapa_atual);

              return (
                <>
                  <span className="text-xs text-muted-foreground mx-1">|</span>
                  <Badge className={cn('text-xs', espConfig.corBadgeBg, espConfig.corBadgeText)}>
                    {espConfig.label}: {currentAreaStatus ? AREA_STATUS_LABELS[currentAreaStatus] : '—'}
                  </Badge>

                  {currentAreaStatus !== 'em_analise' && currentAreaStatus !== 'concluida' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleUpdateAreaStatus('em_analise')}
                      disabled={actionLoading}
                    >
                      Iniciar Análise
                    </Button>
                  )}

                  {currentAreaStatus === 'em_analise' && (
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleUpdateAreaStatus('concluida')}
                      disabled={actionLoading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Concluir Área
                    </Button>
                  )}

                  {currentAreaStatus === 'concluida' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleUpdateAreaStatus('em_analise')}
                      disabled={actionLoading}
                      className="text-xs"
                    >
                      Reabrir Análise
                    </Button>
                  )}
                </>
              );
            })()}
          </div>
        </CardContent>
      </Card>

      {/* Resumo das áreas do grupo atual */}
      {diligencia && (() => {
        const areaStatuses = extractAreaStatuses(diligencia);
        const etapasDoGrupo = getEtapasDoGrupoAtual(diligencia.etapa_atual);
        const etapasComArea = etapasDoGrupo.filter((e) => ETAPA_TO_AREA_STATUS_KEY[e]);
        const grupoAtualKey = mapEtapaToVisual(diligencia.etapa_atual);
        const grupoConcluido = isGrupoVisualConcluido(grupoAtualKey, areaStatuses);
        const { isAdminPrincipal: isPrincipal } = { isAdminPrincipal: !isAdminEspecializado && canAdvanceRetreat };

        // Só mostrar para grupos com áreas (não para nova_solicitacao ou relatorio)
        if (etapasComArea.length === 0) return null;

        const statusIcon = (status: AreaStatus | null) => {
          if (status === 'concluida') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
          if (status === 'em_analise') return <Loader2 className="h-4 w-4 text-blue-500" />;
          return <Circle className="h-4 w-4 text-muted-foreground/40" />;
        };

        const statusColor: Record<AreaStatus, string> = {
          pendente: 'text-muted-foreground',
          em_analise: 'text-blue-600',
          concluida: 'text-green-600 font-medium',
        };

        return (
          <Card>
            <CardContent className="py-4 space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex-1 space-y-2">
                  <span className="text-sm font-medium text-muted-foreground">
                    Áreas do grupo:
                  </span>
                  <div className="space-y-2">
                    {etapasComArea.map((etapa) => {
                      const status = getAreaStatus(areaStatuses, etapa) ?? 'pendente';
                      const temEspecialista = hasEspecialistaForEtapa(etapa);
                      const responsavel = getAreaResponsavelLabel(etapa);

                      return (
                        <div key={etapa} className="flex items-center gap-2 flex-wrap">
                          {statusIcon(status)}
                          <span className="text-sm">
                            {ETAPA_LABELS[etapa]}:
                          </span>
                          <span className={cn('text-sm', statusColor[status])}>
                            {AREA_STATUS_LABELS[status]}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({responsavel})
                          </span>

                          {/* Ações do admin principal para áreas sem especialista */}
                          {isPrincipal && !temEspecialista && status !== 'concluida' && (
                            <>
                              {status === 'pendente' && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  className="h-6 text-xs ml-1"
                                  onClick={() => handleUpdateSingleAreaStatus(etapa, 'em_analise')}
                                  disabled={actionLoading}
                                >
                                  Iniciar
                                </Button>
                              )}
                              {status === 'em_analise' && (
                                <Button
                                  variant="default"
                                  size="sm"
                                  className="h-6 text-xs ml-1 bg-green-600 hover:bg-green-700 text-white"
                                  onClick={() => handleUpdateSingleAreaStatus(etapa, 'concluida')}
                                  disabled={actionLoading}
                                >
                                  Concluir
                                </Button>
                              )}
                            </>
                          )}

                          {/* Reabrir para admin principal */}
                          {isPrincipal && !temEspecialista && status === 'concluida' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 text-xs ml-1"
                              onClick={() => handleUpdateSingleAreaStatus(etapa, 'em_analise')}
                              disabled={actionLoading}
                            >
                              Reabrir
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {grupoConcluido && (
                  <Badge className="bg-green-50 text-green-700 border-green-200 text-xs shrink-0">
                    Grupo concluído
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Layout em duas colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna principal — detalhes */}
        <div className="lg:col-span-2 space-y-6">
          {/* Dados da Incorporadora (oculto para admin especializado) */}
          {!isAdminEspecializado && incorporadoraData && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Dados da Incorporadora
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <InfoField
                    label="Razao Social"
                    value={incorporadoraData.razao_social}
                    icon={<Building2 className="h-4 w-4" />}
                  />
                  <InfoField
                    label="CNPJ"
                    value={incorporadoraData.cnpj}
                    icon={<Hash className="h-4 w-4" />}
                  />
                  <InfoField
                    label="Nome Fantasia"
                    value={incorporadoraData.nome_fantasia}
                    icon={<Building2 className="h-4 w-4" />}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t pt-4">
                  {(incorporadoraData.contato_legal_nome || incorporadoraData.contato_legal_email) && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-3">Representante Legal</p>
                      <div className="space-y-3">
                        <InfoField label="Nome" value={incorporadoraData.contato_legal_nome} icon={<User className="h-4 w-4" />} />
                        <InfoField label="Email" value={incorporadoraData.contato_legal_email} icon={<Mail className="h-4 w-4" />} />
                        <InfoField label="Telefone" value={incorporadoraData.contato_legal_telefone} icon={<Phone className="h-4 w-4" />} />
                      </div>
                    </div>
                  )}
                  {(incorporadoraData.contato_financeiro_nome || incorporadoraData.contato_financeiro_email) && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground mb-3">Contato Financeiro</p>
                      <div className="space-y-3">
                        <InfoField label="Nome" value={incorporadoraData.contato_financeiro_nome} icon={<User className="h-4 w-4" />} />
                        <InfoField label="Email" value={incorporadoraData.contato_financeiro_email} icon={<Mail className="h-4 w-4" />} />
                        <InfoField label="Telefone" value={incorporadoraData.contato_financeiro_telefone} icon={<Phone className="h-4 w-4" />} />
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Botão Editar / Salvar / Cancelar (oculto para admin especializado) */}
          {!isAdminEspecializado && (
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button variant="primary" size="sm" onClick={saveEditing} disabled={isSavingEdit}>
                    {isSavingEdit ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}
                    Salvar Alterações
                  </Button>
                  <Button variant="outline" size="sm" onClick={cancelEditing} disabled={isSavingEdit}>
                    <X className="h-4 w-4 mr-1" />
                    Cancelar
                  </Button>
                </>
              ) : (
                <Button variant="outline" size="sm" onClick={startEditing}>
                  <Pencil className="h-4 w-4 mr-1" />
                  Editar Dados
                </Button>
              )}
            </div>
          )}

          {/* Grid de informacoes: SPE + Empreendimento (oculto para admin especializado) */}
          {!isAdminEspecializado && <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* SPE Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Informacoes da SPE
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <EditableField label="Razão Social" fieldKey="spe_razao_social" icon={<Building2 className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.spe_razao_social} />
                <EditableField label="CNPJ" fieldKey="spe_cnpj" icon={<Hash className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.spe_cnpj} />
                <EditableField label="Endereço Fiscal" fieldKey="spe_endereco_fiscal" icon={<MapPin className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.spe_endereco_fiscal} />
              </CardContent>
            </Card>

            {/* Empreendimento Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Dados do Empreendimento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <EditableField label="Nome" fieldKey="empreendimento_nome" icon={<Building2 className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.empreendimento_nome} />
                <EditableField label="Endereço" fieldKey="empreendimento_endereco" icon={<MapPin className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.empreendimento_endereco} />
                {isEditing ? (
                  <div className="grid grid-cols-2 gap-3">
                    <EditableField label="Cidade" fieldKey="empreendimento_cidade" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.empreendimento_cidade} />
                    <EditableField label="Estado" fieldKey="empreendimento_estado" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.empreendimento_estado} />
                  </div>
                ) : (
                  <InfoField label="Cidade/Estado" value={[diligencia.empreendimento_cidade, diligencia.empreendimento_estado].filter(Boolean).join(' - ') || null} icon={<MapPin className="h-4 w-4" />} />
                )}
                <EditableField label="CEP" fieldKey="empreendimento_cep" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.empreendimento_cep} />
                <EditableField label="Tipo de Imóvel" fieldKey="tipo_imovel" icon={<Building2 className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.tipo_imovel} />
                <EditableField label="Matrículas do Terreno" fieldKey="terreno_matriculas" icon={<FileText className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.terreno_matriculas?.join(', ')} />
              </CardContent>
            </Card>
          </div>}

          {/* Contatos (oculto para admin especializado) */}
          {!isAdminEspecializado && (isEditing || diligencia.contato_representante_nome || diligencia.contato_financeiro_nome) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Contatos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-3">Representante Legal</p>
                    <div className="space-y-3">
                      <EditableField label="Nome" fieldKey="contato_representante_nome" icon={<User className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.contato_representante_nome} />
                      <EditableField label="Email" fieldKey="contato_representante_email" icon={<Mail className="h-4 w-4" />} type="email" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.contato_representante_email} />
                      <EditableField label="Telefone" fieldKey="contato_representante_telefone" icon={<Phone className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.contato_representante_telefone} />
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-3">Contato Financeiro</p>
                    <div className="space-y-3">
                      <EditableField label="Nome" fieldKey="contato_financeiro_nome" icon={<User className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.contato_financeiro_nome} />
                      <EditableField label="Email" fieldKey="contato_financeiro_email" icon={<Mail className="h-4 w-4" />} type="email" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.contato_financeiro_email} />
                      <EditableField label="Telefone" fieldKey="contato_financeiro_telefone" icon={<Phone className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.contato_financeiro_telefone} />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Signatario BravaForte (oculto para admin especializado) */}
          {!isAdminEspecializado && <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="h-5 w-5" />
                Signatario BravaForte
              </CardTitle>
              <CardDescription>
                Defina quem assina pela BravaForte nesta diligencia. Se vazio, usa o padrao (joao@bravaforte.com).
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nome</Label>
                  <Input
                    placeholder="BravaForte Capital LTDA"
                    value={bravaforteNome}
                    onChange={(e) => setBravaforteNome(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="joao@bravaforte.com"
                    value={bravaforteEmail}
                    onChange={(e) => setBravaforteEmail(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleSaveBravaforteSigner}
                  disabled={isSavingSigner}
                >
                  {isSavingSigner ? 'Salvando...' : 'Salvar Signatario'}
                </Button>
              </div>
            </CardContent>
          </Card>}

          {/* Documentos para assinatura */}
          <DiligenciaSignatureDocuments
            documents={isAdminEspecializado
              ? documents.filter((doc) => canAccessEtapa(doc.etapa as DiligenciaEtapa))
              : documents
            }
            onDownload={handleViewDocument}
            onSendToClicksign={handleOpenClicksignModal}
            isSendingClicksign={isSendingClicksign}
            sendingDocumentId={sendingDocumentId}
          />

          {/* Checklist de documentos para revisão por arquivo */}
          {(isAdminEspecializado
            ? etapasPermitidas
            : getEtapasDoGrupoAtual(diligencia.etapa_atual).filter((etapa) => canAccessEtapa(etapa))
          )
            .filter((etapa) => DILIGENCIA_CHECKLISTS[etapa].length > 0)
            .map((etapa) => (
              <DiligenciaDocumentChecklist
                key={etapa}
                diligenciaId={diligencia.id}
                etapa={etapa}
                checklistItems={DILIGENCIA_CHECKLISTS[etapa]}
                documents={documents.filter((d) => d.etapa === etapa)}
                onUpload={async () => ({ uploaded: [], failed: [] })}
                onDelete={async () => false}
                onView={handleViewDocument}
                isUploading={false}
                disabled
                canReview
                onReviewStatusChange={handleReviewDocument}
                isReviewing={reviewLoading}
              />
            ))
          }

          {/* Informacoes Financeiras (oculto para admin especializado) */}
          {!isAdminEspecializado && <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Informacoes Financeiras
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <EditableField label="Valor Alvo da Captação" fieldKey="valor_captacao" icon={<DollarSign className="h-4 w-4" />} format="currency" type="number" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.valor_captacao} />
                <EditableField label="Valor Médio m² no Lançamento" fieldKey="valor_medio_m2_lancamento" icon={<DollarSign className="h-4 w-4" />} format="currency" type="number" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.valor_medio_m2_lancamento} />
                <EditableField label="Somatória m² das Unidades" fieldKey="soma_m2_unidades" icon={<Ruler className="h-4 w-4" />} type="number" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.soma_m2_unidades ? `${Number(diligencia.soma_m2_unidades).toLocaleString('pt-BR')} m²` : null} />
                <EditableField label="Somatória m² Vendável" fieldKey="soma_m2_vendavel" icon={<Ruler className="h-4 w-4" />} type="number" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.soma_m2_vendavel ? `${Number(diligencia.soma_m2_vendavel).toLocaleString('pt-BR')} m²` : null} />
                <EditableField label="Número de Unidades" fieldKey="numero_unidades" icon={<Hash className="h-4 w-4" />} format="number" type="number" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.numero_unidades} />
                <EditableField label="Tamanho das Unidades (m²)" fieldKey="tamanho_unidades_m2" icon={<Ruler className="h-4 w-4" />} isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.tamanho_unidades_m2 ? `${diligencia.tamanho_unidades_m2} m²` : null} />
                <EditableField label="Prazo (meses)" fieldKey="prazo_meses" icon={<Clock className="h-4 w-4" />} type="number" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.prazo_meses ? `${diligencia.prazo_meses} meses` : null} />
                <EditableField label="Lançamento Previsto" fieldKey="data_prevista_lancamento" icon={<Calendar className="h-4 w-4" />} format="date" type="date" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.data_prevista_lancamento} />
                <EditableField label="Entrega Prevista" fieldKey="data_prevista_entrega" icon={<Calendar className="h-4 w-4" />} format="date" type="date" isEditing={isEditing} editData={editData} onChange={updateEditField} originalValue={diligencia.data_prevista_entrega} />
              </div>

              {/* Analise Bravaforte */}
              {(diligencia.equity_vgv_vendavel ||
                diligencia.desconto_efetivo ||
                diligencia.valor_m2_bravaforte) && (
                <>
                  <div className="border-t pt-4 mt-4">
                    <p className="text-sm font-medium text-muted-foreground mb-3">
                      Analise Bravaforte
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoField
                      label="Equity VGV Vendavel"
                      value={
                        diligencia.equity_vgv_vendavel
                          ? `${Number(diligencia.equity_vgv_vendavel).toLocaleString('pt-BR')} m²`
                          : null
                      }
                    />
                    <InfoField
                      label="Desconto Efetivo"
                      value={
                        diligencia.desconto_efetivo
                          ? `${Number(diligencia.desconto_efetivo).toFixed(2)}%`
                          : null
                      }
                    />
                    <InfoField
                      label="Valor m² Bravaforte (SCP)"
                      value={diligencia.valor_m2_bravaforte}
                      format="currency"
                    />
                    <InfoField
                      label="Formato de Retorno SCP"
                      value={diligencia.formato_retorno_scp}
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>}

          {/* Meta Info */}
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap gap-6 text-sm text-muted-foreground">
                <span>Criado em: {formatDate(diligencia.created_at)}</span>
                <span>Atualizado em: {formatDate(diligencia.updated_at)}</span>
                <span>ID: {diligencia.id}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Coluna lateral — painel de atividade */}
        <div className="lg:col-span-1 min-h-[500px]">
          <ActivityPanel
            diligenciaId={diligencia.id}
            currentUserId={user?.id}
          />
        </div>
      </div>

      {/* Modal de envio para ClickSign */}
      <BaseModal
        open={clicksignModalOpen}
        onOpenChange={setClicksignModalOpen}
        title="Enviar para Assinatura (ClickSign)"
        description={`Documento: ${clicksignDocTarget?.file_name || ''}`}
      >
        <div className="space-y-6">
          {clicksignSigners.map((signer, index) => (
            <div key={index} className="space-y-3 p-4 rounded-lg border">
              <p className="text-sm font-medium">
                Signatario {index + 1}
                {index === 0 ? ' (Contratada)' : ' (Contratante)'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Nome</Label>
                  <Input
                    placeholder="Nome completo"
                    value={signer.name}
                    onChange={(e) => updateSigner(index, 'name', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    placeholder="email@exemplo.com"
                    value={signer.email}
                    onChange={(e) => updateSigner(index, 'email', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label>Papel</Label>
                <select
                  className="w-full rounded-md border px-3 py-2 text-sm"
                  value={signer.role}
                  onChange={(e) => updateSigner(index, 'role', e.target.value)}
                >
                  <option value="sign">Assinar</option>
                  <option value="contractor">Contratante</option>
                  <option value="contractee">Contratada</option>
                  <option value="witness">Testemunha</option>
                  <option value="approve">Aprovar</option>
                  <option value="legal_representative">Representante Legal</option>
                </select>
              </div>
            </div>
          ))}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setClicksignModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={handleConfirmClicksignSend}>
              Enviar para ClickSign
            </Button>
          </div>
        </div>
      </BaseModal>
    </div>
  );
};

export default AdminDiligenciaDetalhes;
